import FieldResponse from './FieldResponse';
import {
  getProvisionalIdentifierForType,
} from '../util';
import { createError } from '../error';
import FieldInstance from "./FieldInstance";

class FormResponse {
  constructor(data) {
    data = data || {};

    this.user_role_id = data.user_role_id || null;
    this.current_location = data.current_location || null;

    this.id = data.id || getProvisionalIdentifierForType('formresponse');
    this.events_custom_responses_id = data.events_custom_responses_id || null;
    // this.events_custom_forms_id = data.events_custom_forms_id || null;
    this.product = data.product || null;
    // Server-loaded responses arrive with raw product_labels_id / products_id but
    // no nested product object. Hold the numeric values so the getters below can
    // expose them for product-row matching before the merchandising scope overlay
    // runs and attaches the product. Server JSON has these as strings; the scope
    // service casts to int, so we coerce here to keep === comparisons working.
    this._raw_product_labels_id = data.product_labels_id != null ? Number(data.product_labels_id) || null : null;
    this._raw_products_id       = data.products_id       != null ? Number(data.products_id)       || null : null;
    // Per-instance merchandising activity id (Phase 3). Set by the staff app
    // when the form is a survey type with linked activity types and the user
    // selects one in the per-instance picker. Server-loaded responses
    // already carry this directly. Keep as Number or null.
    this.merchandising_activities_id = data.merchandising_activities_id != null
      ? (Number(data.merchandising_activities_id) || null)
      : null;
    // Per-instance selected supplier (customer). Persisted so editing a
    // submitted recap repopulates the supplier filter. Server-loaded responses
    // carry it directly. Keep as Number or null.
    this.customers_id = data.customers_id != null
      ? (Number(data.customers_id) || null)
      : null;
    this.sampling_feedback = data.sampling_feedback != null
      ? String(data.sampling_feedback)
      : null;
    // Server reads return 't'/'f'; the app round-trips its own booleans.
    this.out_of_stock = data.out_of_stock === true || data.out_of_stock === 't' || data.out_of_stock === 'true';
    this.field_responses = data.field_responses || [];
    this.recapResponse = data.recapResponse || null;
    this.formResponseSet = data.formResponseSet || null;

    this.form = data.form || null;

    if (!this.form) {
      throw createError({
        error: 'MissingForm',
        message: 'Form is required for FormResponse',
      });
    }

    this.events_custom_forms_id = data.form.id;
    this.eventTimezoneName = data.eventTimezoneName || null;
  }

  get products_id() {
    if (this.product) {
      // Merchandising-mode rows store the id on product_labels_id; products_id
      // stays null so the legacy FK on events_custom_forms_responses isn't set.
      if (this.product._merchandising) return null;
      return this.product.id || null;
    }
    return this._raw_products_id;
  }

  get product_labels_id() {
    if (this.product) {
      if (!this.product._merchandising) return null;
      return this.product.id || null;
    }
    return this._raw_product_labels_id;
  }

  get field_responses() {
    return this._field_responses;
  }

  set field_responses(field_responses) {
    this._field_responses = field_responses;
    this._allFieldResponses = null;
  }

  set errors(errors) {
    this._errors = errors;
  }

  get errors() {
    return this._errors;
  }

  get hasErrors() {
    return this.validate().length > 0;
  }

  get visible() {
    // An out-of-stock product may have nothing to fill in, and still has to be sent.
    return this.collectVisibleFieldResponses().length > 0 || !!(this.out_of_stock && this.canMarkOutOfStock);
  }

  set recapResponse(recapResponse) {
    this._recapResponse = recapResponse;
    // Persist to fields
    for (let fieldResponse of this.field_responses) {
      fieldResponse.recapResponse = recapResponse;
    }
  }

  get recapResponse() {
    return this._recapResponse;
  }

  getFieldResponse(fieldInstance) {
    let fieldResponse = this.field_responses.find(response => parseInt(response.events_custom_instances_id) === parseInt(fieldInstance.id));
    if (!fieldResponse) {
      fieldResponse = new FieldResponse({
        events_custom_instances_id: fieldInstance.id,
        fieldInstance: fieldInstance,
        recapResponse: this.recapResponse,
        formResponse: this,
        user_role_id: this.user_role_id,
        current_location: this.current_location,
      });
      this.field_responses.push(fieldResponse);
    }
    return fieldResponse || null;
  }

  getFieldResponsesFromFieldBlock(fieldBlock) {
    return fieldBlock.items.map(fieldInstance => this.getFieldResponse(fieldInstance))
  }

  getOrCreateFieldResponses() {
    if (!this._allFieldResponses) {
      this._allFieldResponses = this.form.items.map(fieldBlockOrInstance => {
        if (fieldBlockOrInstance instanceof FieldInstance) {
          // We have a field instance, so find its matching FieldResponse
          return this.getFieldResponse(fieldBlockOrInstance);
        }
        // The item is a Field Block, so we need to get the matching field response from the field block
        return this.getFieldResponsesFromFieldBlock(fieldBlockOrInstance);
      }).reduce((acc, fieldResponseOrResponses) => {
        if (Array.isArray(fieldResponseOrResponses)) {
          // The item was a field block, so we need to get all the field responses we found for it
          return acc.concat(...fieldResponseOrResponses);
        }
        // The item was just a field response, so push it
        acc.push(fieldResponseOrResponses);
        return acc;
      }, []);
    }
    return this._allFieldResponses;
  }

  collectValues() {
      const validatableFieldResponses = this.collectVisibleFieldResponses();
      return validatableFieldResponses.map(fieldResponse => fieldResponse.collectValues());
  }

  get isRequiredProduct() {
    if (!this.form || !this.form.isSamplingForm || !this.product_labels_id) return false;
    const required = Array.isArray(this.form._merchandisingDefaultProducts) ? this.form._merchandisingDefaultProducts : [];
    const isRequiredLabel = p => Number(p && p.id) === Number(this.product_labels_id);
    if (!required.some(isRequiredLabel)) return false;
    // Only the first row of a required product is locked, so a manually added duplicate can be removed.
    const responses = this.formResponseSet && Array.isArray(this.formResponseSet.form_responses) ? this.formResponseSet.form_responses : null;
    if (!responses) return true;
    const first = responses.find(r => Number(r && r.product_labels_id) === Number(this.product_labels_id));
    return first === this;
  }

  get canMarkOutOfStock() {
    return this.isRequiredProduct;
  }

  validate() {
    if (this.out_of_stock && this.canMarkOutOfStock) {
      this.errors = [];
      return this.errors;
    }
    const validatableFieldResponses = this.collectVisibleFieldResponses();
    this.errors = validatableFieldResponses
      .map(fieldResponse => fieldResponse.validate())
      .reduce((pre, cur) => pre.concat(cur), [])
      .concat(this.validateMerchandisingStaticFields());
    return this.errors;
  }

  updateDisplayLogic(changedFieldResponses) {
    const results = this.getOrCreateFieldResponses().map(fieldResponse => fieldResponse.updateDisplayLogic(changedFieldResponses));
    return results.some(result => result);
  }

  updateLogicModifiers() {
    for (const fieldResponse of this.field_responses) {
      fieldResponse.checkLogicModifiers();
    }
  }

  collectVisibleFieldResponses() {
    return this.getOrCreateFieldResponses()
      .filter(fieldResponse => fieldResponse.visible)
      .filter(fieldResponse => this.matchesMerchandisingActivity(fieldResponse));
  }

  /**
   * Whether a field response is shown for the currently selected merchandising
   * activity type. Fields can be assigned to specific activities
   * (fieldInstance.merchandising_activities); an assigned field only shows when
   * the selected activity is in its list. Unassigned fields, and all fields on
   * non-merchandising forms, always match.
   *
   * This is the single source of truth for merch activity-type visibility, used
   * by both rendering and validation/serialization so a field hidden by the
   * selected activity type can never block save as a "required" field it isn't.
   */
  matchesMerchandisingActivity(fieldResponse) {
    if (!this.form || !this.form.isMerchandisingForm) {
      return true;
    }
    const fieldInstance = fieldResponse && fieldResponse.fieldInstance;
    const activityIds = ((fieldInstance && fieldInstance.merchandising_activities) || [])
      .map(a => Number(a && a.id))
      .filter(id => Number.isFinite(id) && id > 0);
    // Unassigned field: shows for every activity type on this form.
    if (activityIds.length === 0) {
      return true;
    }
    const selectedId = Number(this.merchandising_activities_id) || null;
    // Activity-restricted field with no activity chosen yet: hide until one is.
    if (!selectedId) {
      return false;
    }
    return activityIds.indexOf(selectedId) !== -1;
  }

  /**
   * Activity types available for this instance's activity-type picker. Sorted by
   * name for stable UI. Mirrors the per-instance picker: every active activity
   * type for the event's program (overlaid as _merchandisingEventActivities);
   * the form-/field-level merchandising_activities assignments only gate field
   * visibility, not this option list.
   *
   * Single source of truth for the recapResponseFormResponse controller getter
   * of the same name AND for validation - so a picker shown to the user is the
   * same picker validate() enforces.
   *
   * @returns {{id:number,name:string}[]}
   */
  get merchActivityTypeOptions() {
    if (!this.form) return [];
    const eventActivities = Array.isArray(this.form._merchandisingEventActivities)
      ? this.form._merchandisingEventActivities
      : null;
    if (eventActivities && eventActivities.length) {
      return eventActivities
        .filter(a => a && Number.isFinite(Number(a.id)) && Number(a.id) > 0)
        .map(a => ({ id: Number(a.id), name: String(a.name || '') }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    // Fallback when no scope overlay is present (e.g. a non-merchandising
    // context): the form's configured types, restricted to the event's set.
    const activities = (this.form.merchandising_activities) || [];
    const eventIds = Array.isArray(this.form._merchandisingEventActivityIds)
      ? this.form._merchandisingEventActivityIds.map(Number)
      : null;
    return activities
      .filter(a => a && Number.isFinite(Number(a.id)) && Number(a.id) > 0)
      .filter(a => !eventIds || eventIds.indexOf(Number(a.id)) !== -1)
      .map(a => ({ id: Number(a.id), name: String(a.name || '') }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Whether the activity-type picker is shown for this instance. With a single
   * linked activity type the value is auto-set by the picker and the picker
   * stays hidden; with two or more it's shown so the merchandiser picks one.
   *
   * @returns {boolean}
   */
  get merchShowActivityTypePicker() {
    if (!this.form || !this.form.isMerchandisingForm) return false;
    return this.merchActivityTypeOptions.length >= 2;
  }

  /**
   * Whether the selected activity type uses the product picker. An activity with
   * show_product_selection === false (e.g. New Account) hides it. No/unknown
   * activity selected defaults to allowing products.
   *
   * @returns {boolean}
   */
  get merchSelectedActivityAllowsProducts() {
    const id = this.merchandising_activities_id
      ? Number(this.merchandising_activities_id)
      : null;
    if (!id) return true;
    const acts = (this.form && this.form._merchandisingEventActivities) || [];
    const act = acts.find(a => Number(a && a.id) === id);
    if (!act) return true;
    return act.show_product_selection !== false;
  }

  /**
   * Products available to pick on this instance, sourced from the form's
   * overlaid products pool (seeded from the event's merchandising scope).
   *
   * @returns {{id:number,name:string}[]}
   */
  get merchProductOptions() {
    if (!this.form) return [];
    const products = (this.form.products) || [];
    return products
      .filter(p => p && Number.isFinite(Number(p.id)) && Number(p.id) > 0)
      .map(p => ({ id: Number(p.id), name: String(p.name || '') }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Whether the product picker is shown for this instance. On any product-scope
   * form (merchandising or sampling), when the selected activity allows products
   * (always true for sampling, which has no activity) and the product pool is
   * non-empty.
   *
   * @returns {boolean}
   */
  get merchShowProductPicker() {
    if (!this.form || !this.form.isProductScopeForm) return false;
    if (!this.merchSelectedActivityAllowsProducts) return false;
    return !!this.form._merchandisingSurvey && this.merchProductOptions.length > 0;
  }

  /**
   * True when the activity-type picker is shown but no activity type is chosen.
   * The static activity-type field is required whenever its picker is visible.
   *
   * @returns {boolean}
   */
  get merchActivityTypeMissing() {
    if (!this.merchShowActivityTypePicker) return false;
    return !this.merchandising_activities_id;
  }

  /**
   * True when the product picker is shown but no product is chosen. The static
   * product field is required whenever its picker is visible.
   *
   * @returns {boolean}
   */
  get merchProductMissing() {
    if (!this.merchShowProductPicker) return false;
    return !this.product_labels_id;
  }

  /**
   * Validation for the built-in static merchandising fields (activity type and
   * product) that sit at the top of each merch instance. These live on the
   * response row itself (merchandising_activities_id / product_labels_id), not
   * in the events_custom field system, so they are NOT covered by the
   * field-response validation pass and must be checked here. A field is only
   * required when its picker is actually shown (see merchShow* getters), so a
   * hidden/auto-set picker never blocks save.
   *
   * @returns {{key:string,message:string}[]}
   */
  validateMerchandisingStaticFields() {
    const errors = [];
    if (this.merchActivityTypeMissing) {
      errors.push({ key: 'ACTIVITY_TYPE_REQUIRED', message: 'Activity type is required' });
    }
    if (this.merchProductMissing) {
      errors.push({ key: 'PRODUCT_REQUIRED', message: 'Product is required' });
    }
    return errors;
  }
}

export default FormResponse;
